import type { DraftStep } from "@zworks/console-contracts";
import * as React from "react";
import { type NodeLayout } from "@/lib/flow-layout";

export function CanvasMinimap({
  steps,
  layouts,
  panX,
  panY,
  scale,
  containerWidth,
  containerHeight,
  onNavigate,
}: {
  steps: DraftStep[];
  layouts: Map<string, NodeLayout>;
  panX: number;
  panY: number;
  scale: number;
  containerWidth: number;
  containerHeight: number;
  onNavigate: (x: number, y: number) => void;
}) {
  const mapWidth = 160;
  const mapHeight = 100;

  // Compute total graph bounding box
  let minX = 0;
  let maxX = 800;
  let minY = 0;
  let maxY = 400;

  layouts.forEach((layout) => {
    minX = Math.min(minX, layout.position.x);
    maxX = Math.max(maxX, layout.position.x + layout.position.width);
    minY = Math.min(minY, layout.position.y);
    maxY = Math.max(maxY, layout.position.y + layout.position.height);
  });

  const contentW = Math.max(maxX - minX + 200, 1000);
  const contentH = Math.max(maxY - minY + 200, 600);

  const scaleX = mapWidth / contentW;
  const scaleY = mapHeight / contentH;
  const mapScale = Math.min(scaleX, scaleY);

  // Viewport box relative to minimap
  const viewX = (-panX - minX + 100) * mapScale;
  const viewY = (-panY - minY + 100) * mapScale;
  const viewW = (containerWidth / scale) * mapScale;
  const viewH = (containerHeight / scale) * mapScale;

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const targetContentX = clickX / mapScale + minX - 100;
    const targetContentY = clickY / mapScale + minY - 100;

    onNavigate(-targetContentX + containerWidth / 2, -targetContentY + containerHeight / 2);
  };

  return (
    <div
      aria-label="Minimap"
      className="absolute bottom-4 right-4 z-30 hidden overflow-hidden rounded-lg border bg-card/90 p-1 shadow-md backdrop-blur-sm sm:block"
    >
      <svg
        width={mapWidth}
        height={mapHeight}
        onClick={handleClick}
        className="cursor-crosshair rounded bg-muted/40"
      >
        {/* Render node miniatures */}
        {steps.map((step) => {
          const layout = layouts.get(step.id);
          if (!layout) return null;
          const nx = (layout.position.x - minX + 100) * mapScale;
          const ny = (layout.position.y - minY + 100) * mapScale;
          const nw = layout.position.width * mapScale;
          const nh = layout.position.height * mapScale;

          return (
            <rect
              key={`mini-${step.id}`}
              x={nx}
              y={ny}
              width={nw}
              height={nh}
              rx={2}
              className={step.gate ? "fill-warning/80" : "fill-primary/80"}
            />
          );
        })}

        {/* Viewport Frame */}
        <rect
          x={Math.max(0, viewX)}
          y={Math.max(0, viewY)}
          width={Math.min(mapWidth, viewW)}
          height={Math.min(mapHeight, viewH)}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-accent-text"
        />
      </svg>
    </div>
  );
}
